import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Manages focus on route changes for accessibility.
 *
 * This hook implements WCAG 2.4.3 (Focus Order) best practices by automatically
 * moving focus to the main page heading when navigation occurs. This ensures:
 * - Screen readers announce the new page title
 * - Keyboard users have clear context of where they are
 * - Focus doesn't remain on elements from the previous page
 *
 * @see https://www.w3.org/WAI/WCAG21/Understanding/focus-order.html
 *
 * Usage:
 * Call this hook once at the app root level (e.g., in App component or main layout).
 * It will automatically handle focus management for all route changes.
 *
 * @example
 * function App() {
 *   useFocusOnRouteChange()
 *   return <Routes>...</Routes>
 * }
 */
export function useFocusOnRouteChange(): void {
  const location = useLocation()
  const prevPathname = useRef(location.pathname)

  useEffect(() => {
    const pathnameChanged = prevPathname.current !== location.pathname
    prevPathname.current = location.pathname

    // Avoid stealing focus on initial page load or when React re-runs effects
    // in development — preserving the skip link as the first Tab stop (WCAG 2.4.1).
    if (!pathnameChanged) {
      return
    }

    // Blur any currently focused element to prevent lingering focus states
    // This is necessary for dropdowns/buttons that retain focus after navigation
    const activeElement = document.activeElement
    if (
      activeElement instanceof HTMLElement &&
      activeElement !== document.body
    ) {
      activeElement.blur()
    }

    // Try to find the main page heading (h1) or main content area as fallback
    // Priority: h1 heading (best for screen readers) > main content container
    const mainHeading = document.querySelector('main h1, [role="main"] h1, h1')
    const mainContent = document.querySelector('main, [role="main"]')
    const elementToFocus = mainHeading || mainContent

    if (elementToFocus instanceof HTMLElement) {
      // Make the element programmatically focusable
      // tabindex="-1" means it can receive focus via JS but not via Tab key
      if (!elementToFocus.hasAttribute('tabindex')) {
        elementToFocus.setAttribute('tabindex', '-1')
      }

      // Move focus to the element
      // This will cause screen readers to announce the heading text or main content
      // `preventScroll` stops the browser from scrolling the focused heading into
      // view — without it, navigating into an async-rendering page (e.g. /home,
      // whose heading sits in a sticky PageHeader) nudges the scroll container
      // down and clips the top of the page on short viewports (CORE-5990).
      elementToFocus.focus({ preventScroll: true })

      // Remove visual focus indicator after programmatic focus
      // Since this element has tabindex="-1", users won't reach it via Tab anyway
      // The outline is only hidden for this programmatic focus, maintaining WCAG 2.4.7
      elementToFocus.style.outline = 'none'
    }

    // Cleanup is not needed as focus moves naturally with user interaction
  }, [location.pathname]) // Re-run when route changes
}
